import { CallAdapter } from './callAdapter';

export class NativeWebRTCAdapter extends CallAdapter {
  constructor({ sendSignal }) {
    super();
    this.sendSignal = sendSignal;
    this.remoteStream = null;
    this.iceCandidatesQueue = [];
  }

  async connect({ isCaller, conversationId }) {
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,  // Eliminates hearing your own voice back (echo)
        noiseSuppression: true,   // Filters out background fan/hum noise
        autoGainControl: true     // Normalizes speaker volume
      }
    });
    
    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        {
          urls: [
            'turn:20.46.147.126:3478?transport=udp',
            'turn:20.46.147.126:3478?transport=tcp',
            'stun:20.46.147.126:3478'
          ],
          username: 'webrtcuser',
          credential: 'webrtcpassword123'
        }
      ]
    });

    // Log connection states to help debug network drops
    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('WebRTC: ICE Connection State changed to:', this.peerConnection.iceConnectionState);
    };
    this.peerConnection.onconnectionstatechange = () => {
      console.log('WebRTC: Connection State changed to:', this.peerConnection.connectionState);
    };
    this.peerConnection.onsignalingstatechange = () => {
      console.log('WebRTC: Signaling State changed to:', this.peerConnection.signalingState);
    };

    // Add local tracks to connection
    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });

    // Handle remote track stream
    this.peerConnection.ontrack = (event) => {
      this.remoteStream = event.streams[0];
      this.dispatchEvent('track', this.remoteStream);
    };

    // Gather ICE network candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignal({
          conversation_id: conversationId,
          signal: { type: 'candidate', candidate: event.candidate }
        });
      }
    };

    if (isCaller) {
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      this.sendSignal({
        conversation_id: conversationId,
        signal: { type: 'offer', sdp: offer.sdp }
      });
    }
  }

  async handleSignal(signal) {
    if (!this.peerConnection) return;

    if (signal.type === 'offer') {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal));
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      this.sendSignal({
        signal: { type: 'answer', sdp: answer.sdp }
      });
      await this.processQueuedCandidates();
    } else if (signal.type === 'answer') {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal));
      await this.processQueuedCandidates();
    } else if (signal.type === 'candidate') {
      if (this.peerConnection.remoteDescription) {
        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(signal.candidate));
        } catch (e) {
          console.error("Error adding ice candidate:", e);
        }
      } else {
        this.iceCandidatesQueue.push(signal.candidate);
      }
    }
  }

  async processQueuedCandidates() {
    while (this.iceCandidatesQueue.length > 0) {
      const candidate = this.iceCandidatesQueue.shift();
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.error("Error adding queued ice candidate:", e);
      }
    }
  }

  async disconnect() {
    if (this.peerConnection) {
      this.peerConnection.onicecandidate = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.onsignalingstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    this.remoteStream = null;
    this.iceCandidatesQueue = [];
    this.dispatchEvent('disconnected');
    this.listeners = {};
  }

  setMuted(shouldMute) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !shouldMute;
      });
    }
  }
}
